// AYA PRO — Dashboard commerçant : configuration (Réf 5, fiche non publiée) puis opérationnel (Réf 6).
// Chiffres 100 % réels (stats dérivées des commandes) — compte neuf = zéros / états vides.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Bell, MapPin, Pencil, Store, UserRound, ShoppingCart, Users, BarChart3, Eye, ChevronRight, Plus, Box, FileText, CalendarDays, Megaphone, Settings, Camera, Tag, Home, Package, ClipboardList, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AyaProLogo } from "@/components/pro/AyaProLogo";
import { InfosEditor, PhotosEditor, ProductsEditor, DeliveryEditor, PublishEditor } from "./AyaProShopEditors";
import "./ayapro.css";
import "./apdash.css";

const API = process.env.REACT_APP_BACKEND_URL;
const fcfa = (n) => `${Number(n || 0).toLocaleString("fr-FR").replace(/\u202f/g, " ")}`;
const STEPS = [
  { key: "infos", label: "Informations" }, { key: "photos", label: "Photos" },
  { key: "produits", label: "Produits & tarifs" }, { key: "livraison", label: "Zone de livraison" },
  { key: "publication", label: "Publication" },
];
const STATUS_LABEL = { en_attente: "En attente", en_cours: "En cours", livree: "Livrée", annulee: "Annulée" };
const NEXT_STATUS = { en_attente: "en_cours", en_cours: "livree" };
const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const relTime = (iso) => {
  const d = new Date(iso); const h = (Date.now() - d) / 3600000;
  if (h < 24) return `Aujourd'hui à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
  if (h < 48) return `Hier à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) + ` à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
};

export default function AyaProDashboardPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("aya_pro_token") || sessionStorage.getItem("aya_pro_token");
  const [account, setAccount] = useState(null);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [ordersList, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("accueil");
  const [editor, setEditor] = useState(null);
  const [liveSheet, setLiveSheet] = useState(false);
  const [liveData, setLiveData] = useState({ live: null, live_orders: [] });
  const [liveTitle, setLiveTitle] = useState("");

  const h = useMemo(() => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }), [token]);
  const api = useCallback(async (path, opts = {}) => {
    const r = await fetch(`${API}/api/ayapro${path}`, { ...opts, headers: h });
    if (r.status === 401) { navigate("/pro/connexion"); throw new Error("session"); }
    return r.json();
  }, [h, navigate]);

  const loadAll = useCallback(async () => {
    const [me, sh, pr, od, st] = await Promise.all([
      api("/me"), api("/shop/me"), api("/shop/products"), api("/shop/orders"), api("/shop/stats"),
    ]);
    if (me.ok) setAccount(me.account);
    if (sh.ok) setShop(sh.shop);
    if (pr.ok) setProducts(pr.products);
    if (od.ok) setOrders(od.orders);
    if (st.ok) setStats(st.stats);
  }, [api]);

  useEffect(() => {
    if (!token) { navigate("/pro/connexion"); return; }
    loadAll().catch(() => {});
  }, [token, navigate, loadAll]);

  // LIVE — rafraîchissement temps réel quand la console est ouverte ou qu'un direct tourne.
  const refreshLive = useCallback(async () => {
    try { const j = await api("/shop/live/me"); if (j.ok) setLiveData({ live: j.live, live_orders: j.live_orders || [] }); } catch { /* session */ }
  }, [api]);
  useEffect(() => { refreshLive(); }, [refreshLive]);
  useEffect(() => {
    if (!liveSheet && !liveData.live) return undefined;
    const t = setInterval(refreshLive, 5000);
    return () => clearInterval(t);
  }, [liveSheet, liveData.live, refreshLive]);

  const startLive = async () => {
    const j = await api("/shop/live/start", { method: "POST", body: JSON.stringify({ title: liveTitle || undefined }) });
    if (j.ok) { setLiveData((p) => ({ ...p, live: j.live })); toast.success("🔴 Vous êtes en direct !"); }
    else toast.error("Publiez d'abord votre boutique pour lancer un LIVE");
  };
  const stopLive = async () => {
    const j = await api("/shop/live/stop", { method: "POST" });
    if (j.ok) { setLiveData({ live: null, live_orders: [] }); toast.success("Direct terminé"); }
  };
  const pinProduct = async (pid) => {
    const j = await api("/shop/live/pin", { method: "POST", body: JSON.stringify({ product_id: pid }) });
    if (j.ok) { refreshLive(); toast.success(pid ? "Produit mis en avant" : "Produit retiré"); }
  };

  const saveShop = async (patch) => {
    const j = await api("/shop/me", { method: "PUT", body: JSON.stringify(patch) });
    if (j.ok) { setShop(j.shop); setEditor(null); toast.success("Enregistré ✓"); } else toast.error("Enregistrement impossible");
  };
  const createProduct = async (f) => {
    const j = await api("/shop/products", { method: "POST", body: JSON.stringify({ ...f, price: Number(f.price) }) });
    if (j.ok) { setProducts((p) => [j.product, ...p]); setShop((s) => ({ ...s, progress: { ...s.progress, produits: true }, progress_done: Math.max(s.progress_done, 1) })); toast.success("Produit ajouté"); loadAll(); }
  };
  const updateProduct = async (id, patch) => {
    const j = await api(`/shop/products/${id}`, { method: "PUT", body: JSON.stringify(patch.price ? { ...patch, price: Number(patch.price) } : patch) });
    if (j.ok) setProducts((p) => p.map((x) => (x.id === id ? j.product : x)));
  };
  const deleteProduct = async (id) => {
    const j = await api(`/shop/products/${id}`, { method: "DELETE" });
    if (j.ok) { setProducts((p) => p.filter((x) => x.id !== id)); loadAll(); }
  };
  const publish = async () => {
    const j = await api("/shop/publish", { method: "POST" });
    if (j.ok) { toast.success("🎉 Votre boutique est publiée sur AYA !"); setEditor(null); loadAll(); }
    else toast.error("Terminez d'abord les étapes de configuration");
  };
  const advanceOrder = async (o) => {
    const next = NEXT_STATUS[o.status];
    if (!next) return;
    const j = await api(`/shop/orders/${o.id}/status`, { method: "PATCH", body: JSON.stringify({ status: next }) });
    if (j.ok) { setOrders((l) => l.map((x) => (x.id === o.id ? { ...x, status: next } : x))); toast.success(`Commande ${STATUS_LABEL[next].toLowerCase()}`); loadAll(); }
  };

  if (!shop) return <div className="apd-page" style={{ display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="apd-loading">Chargement de votre espace…</div>;

  const published = shop.status === "published";
  const firstName = (account?.owner_name || shop.name || "").split(" ")[0];
  const prog = shop.progress || {};
  const doneCount = ["infos", "photos", "produits", "livraison", "publication"].filter((k) => prog[k]).length;
  const nextStep = STEPS.find((s) => !prog[s.key])?.key || "publication";
  const openPreview = () => navigate(`/boutique/${shop.slug}`);
  const clients = Object.values(ordersList.reduce((m, o) => {
    if (!m[o.customer_phone]) m[o.customer_phone] = { name: o.customer_name, phone: o.customer_phone, count: 0, total: 0 };
    m[o.customer_phone].count += 1; if (o.status !== "annulee") m[o.customer_phone].total += o.total;
    return m;
  }, {}));
  const maxBar = Math.max(1, ...(stats?.week_series || []).map((d) => d.total));

  const Shortcuts = (
    <div className="apd-shortcuts" data-testid="apd-shortcuts">
      <button className="apd-sc" style={{ background: "#E8F8EF" }} onClick={() => setEditor("produits")} data-testid="sc-add-product"><span className="ic" style={{ background: "#0E8A4F", color: "#fff" }}><Plus size={18} /></span>Ajouter un produit</button>
      <button className="apd-sc" style={{ background: "#FDF3E3" }} onClick={() => setEditor("produits")} data-testid="sc-stock"><span className="ic"><Box size={17} color="#F0590B" /></span>Gérer mon stock</button>
      <button className="apd-sc" style={{ background: "#EAF2FB" }} onClick={() => setTab("commandes")} data-testid="sc-orders"><span className="ic"><FileText size={17} color="#2563EB" /></span>{published ? "Mes commandes" : "Mes demandes"}</button>
      <button className="apd-sc" style={{ background: "#FBEAF0" }} onClick={() => toast.info("Rendez-vous — bientôt disponible")} data-testid="sc-rdv"><span className="ic"><CalendarDays size={17} color="#DB2777" /></span>Mes rendez-vous</button>
      <button className="apd-sc" style={{ background: "#EDF0FB" }} onClick={() => toast.info("Promotion — bientôt disponible")} data-testid="sc-promo"><span className="ic"><Megaphone size={17} color="#6D28D9" /></span>Promouvoir mon activité</button>
      <button className="apd-sc" style={{ background: "#F3F4F6" }} onClick={() => setEditor("infos")} data-testid="sc-settings"><span className="ic"><Settings size={17} color="#374151" /></span>Paramètres du compte</button>
    </div>
  );

  const Yango = (
    <button className="apd-banner" style={{ background: "#FBEAEA" }} onClick={() => toast.info("Option Yango affichée à titre indicatif — l'intégration livraison n'est pas encore configurée.")} data-testid="apd-yango">
      <span className="ic">🚗</span>
      <div><b>{published ? "Proposez la livraison à domicile par Yango" : "Activez la livraison à domicile"}</b><span>Livrez vos produits rapidement à vos clients grâce à Yango.</span></div>
      <ChevronRight size={16} style={{ marginLeft: "auto", flexShrink: 0 }} />
    </button>
  );

  const ProfileCard = (
    <div className="apd-profile" data-testid="apd-profile-card">
      {shop.banner_image && <img className="banner" src={shop.banner_image} alt="" />}
      <div className="inner">
        <span className="logo">{shop.logo_image ? <img src={shop.logo_image} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : "🏪"}</span>
        <div style={{ minWidth: 0 }}>
          <h2>{shop.name} <button onClick={() => setEditor("infos")} style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280" }} aria-label="Modifier" data-testid="apd-edit-name"><Pencil size={14} /></button></h2>
          {shop.description && <div className="sub">{shop.description}</div>}
          {shop.tagline && !shop.description && <div className="sub">{shop.tagline}</div>}
          {shop.city && <span className="loc"><MapPin size={12} color="#0E8A4F" /> {shop.city}</span>}
          <div>
            <span className={`apd-status ${published ? "live" : "draft"}`} data-testid="apd-status-badge">{published ? "● Actif" : "⏳ Fiche en cours de création"}</span>
          </div>
          {published && <button className="apd-editbtn" onClick={() => setEditor("infos")} data-testid="apd-edit-fiche"><Pencil size={13} /> Modifier ma fiche</button>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="apd-page" data-testid="ayapro-dashboard">
      <div className="apd-wrap">
        <div className="apd-top">
          <button style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => navigate("/pro/bienvenue")} aria-label="Menu" data-testid="apd-burger"><Menu size={22} /></button>
          <AyaProLogo variant="onboarding" onLight />
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button style={{ background: "none", border: "none", cursor: "pointer", position: "relative" }} onClick={() => setTab("commandes")} aria-label="Notifications" data-testid="apd-bell">
              <Bell size={20} />
              {ordersList.filter((o) => o.status === "en_attente").length > 0 && <span style={{ position: "absolute", top: -4, right: -4, minWidth: 15, height: 15, borderRadius: 99, background: "#DC2626", color: "#fff", fontSize: 9, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>{ordersList.filter((o) => o.status === "en_attente").length}</span>}
            </button>
            <span className="av">{shop.logo_image ? <img src={shop.logo_image} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : <UserRound size={17} color="#0E8A4F" />}</span>
          </div>
        </div>

        {tab === "accueil" && (
          <>
            <div className="apd-hello">
              <div>
                <h1 data-testid="apd-greeting">Bonjour {firstName} ! 👋</h1>
                <p>{published ? "Gérez votre activité, recevez des commandes et développez votre commerce sur AYA." : "Votre compte est activé, il reste quelques étapes pour rendre votre fiche visible sur AYA."}</p>
              </div>
              {published ? (
                <button className="apd-fichecard green" onClick={openPreview} data-testid="apd-public-card"><span className="ic">🏪</span><div><b>Ma fiche publique</b><small>Voir ma fiche sur AYA</small></div><ChevronRight size={15} /></button>
              ) : (
                <button className="apd-fichecard" onClick={() => setEditor(nextStep)} data-testid="apd-progress-card">
                  <span className="ic">🏪</span>
                  <div><b>Complétez votre fiche</b><div className="bar"><i style={{ width: `${(doneCount / 5) * 100}%` }} /></div></div>
                  <b style={{ fontSize: 13 }} data-testid="apd-progress-count">{doneCount}/5</b>
                </button>
              )}
            </div>

            {ProfileCard}

            {!published && (
              <div className="apd-progress" data-testid="apd-config-steps">
                <h3><span className="lead"><UserRound size={18} /></span> Terminez votre fiche pour attirer plus de clients</h3>
                <p>Suivez les étapes ci-dessous pour être visible sur AYA et recevoir vos premières commandes.</p>
                <div className="apd-track">
                  {STEPS.map((s, i) => {
                    const done = prog[s.key];
                    const isNext = s.key === nextStep;
                    return (
                      <div key={s.key} style={{ display: "contents" }}>
                        {i > 0 && <span className={`apd-link ${prog[STEPS[i - 1].key] ? "done" : ""}`} />}
                        <button className={`apd-st ${done ? "done" : isNext ? "next" : ""}`} onClick={() => setEditor(s.key)} data-testid={`apd-step-${s.key}`}>
                          <span className="dot">{done ? "✓" : i + 1}</span>
                          <b>{s.label}</b>
                          <small>{done ? "Complété" : isNext ? "À faire" : "À venir"}</small>
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button className="apd-ctamain" onClick={() => setEditor(nextStep)} data-testid="apd-continue-config">Continuer la configuration <ChevronRight size={17} /></button>
              </div>
            )}

            {published && (
              <button className="apd-banner" style={{ background: liveData.live ? "#111827" : "#FEE2E2", color: liveData.live ? "#fff" : "#111827" }} onClick={() => setLiveSheet(true)} data-testid="apd-live-banner">
                <span className="ic" style={{ background: liveData.live ? "#DC2626" : "#fff", color: "#fff" }}>🔴</span>
                <div>
                  <b style={{ color: liveData.live ? "#fff" : "#111827" }}>{liveData.live ? "LIVE en cours — Gérer mon direct" : "LIVE Boutique — Lancer un direct"}</b>
                  <span style={{ color: liveData.live ? "#D1D5DB" : "#4B5563" }}>{liveData.live ? `👁 ${liveData.live.viewers} spectateurs · ❤️ ${liveData.live.reactions} · ${liveData.live_orders.length} commande(s) live` : "Mettez un produit en avant et vendez en temps réel."}</span>
                </div>
                <ChevronRight size={16} style={{ marginLeft: "auto", flexShrink: 0 }} />
              </button>
            )}

            {published && stats && (
              <div className="apd-kpis" data-testid="apd-kpis">
                <button className="apd-kpi" style={{ background: "#E8F8EF" }} onClick={() => setTab("commandes")} data-testid="kpi-orders"><span className="ic"><ShoppingCart size={17} color="#0E8A4F" /></span><b>{stats.orders_week}</b><span>Commandes cette semaine</span></button>
                <button className="apd-kpi" style={{ background: "#FDF3E3" }} onClick={() => setTab("clients")} data-testid="kpi-clients"><span className="ic"><Users size={17} color="#F0590B" /></span><b>{stats.clients_month}</b><span>Clients ce mois</span></button>
                <button className="apd-kpi" style={{ background: "#EAF2FB" }} onClick={() => setTab("stats")} data-testid="kpi-revenue"><span className="ic"><BarChart3 size={17} color="#2563EB" /></span><b>{fcfa(stats.revenue_month)} <span style={{ fontSize: 11 }}>FCFA</span></b><span>Chiffre d'affaires ce mois</span></button>
                <button className="apd-kpi" style={{ background: "#E9F7FB" }} onClick={openPreview} data-testid="kpi-views"><span className="ic"><Eye size={17} color="#0891B2" /></span><b>{fcfa(stats.views)}</b><span>Vues de ma fiche</span></button>
              </div>
            )}

            {Shortcuts}

            {published ? (
              <>
                <div className="apd-sec" data-testid="apd-recent-orders">
                  <div className="head"><h3>Commandes récentes</h3><button onClick={() => setTab("commandes")} data-testid="apd-orders-all">Voir tout <ChevronRight size={13} /></button></div>
                  {ordersList.length === 0 && <div className="apd-empty">Aucune commande pour le moment.<br />Partagez votre boutique pour recevoir vos premières commandes !</div>}
                  {ordersList.slice(0, 4).map((o) => (
                    <div className="apd-order" key={o.id} data-testid={`order-${o.id}`}>
                      <span className="av">{initials(o.customer_name)}</span>
                      <div className="nm"><b>{o.customer_name}</b><span>{o.items.map((i) => `${i.qty} ${i.name}`).join(", ").slice(0, 38)} · {relTime(o.created_at)}</span></div>
                      <button className={`apd-chip ${o.status}`} onClick={() => advanceOrder(o)} title={NEXT_STATUS[o.status] ? `Passer à « ${STATUS_LABEL[NEXT_STATUS[o.status]]} »` : ""} data-testid={`order-status-${o.id}`}>● {STATUS_LABEL[o.status]}</button>
                    </div>
                  ))}
                </div>
                <div className="apd-sec" data-testid="apd-my-products">
                  <div className="head"><h3>Mes produits</h3><button onClick={() => setEditor("produits")} data-testid="apd-products-all">Voir tout <ChevronRight size={13} /></button></div>
                  {products.length === 0 && <div className="apd-empty">Aucun produit — ajoutez-en pour vendre sur AYA.</div>}
                  <div className="apd-prods">
                    {products.map((p) => (
                      <div className="apd-prod" key={p.id}>
                        {p.image ? <img src={p.image} alt="" /> : <div style={{ height: 76, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>🛒</div>}
                        <div className="b"><b>{p.name}</b><div className="pr">{fcfa(p.price)} FCFA</div>
                          <button className={`st ${p.in_stock ? "" : "off"}`} onClick={() => updateProduct(p.id, { in_stock: !p.in_stock })} data-testid={`stock-${p.id}`}>{p.in_stock ? "En stock" : "Rupture"}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="apd-sec" data-testid="apd-week-perf">
                  <div className="head"><h3>Performance de la semaine</h3><span style={{ fontSize: 11, color: "#6B7280", fontWeight: 800 }}>7 derniers jours</span></div>
                  <div className="apd-chart">
                    {(stats?.week_series || []).map((d, i) => (
                      <div className="apd-bar" key={i} title={`${d.day} : ${fcfa(d.total)} FCFA`}>
                        <i style={{ height: `${Math.max(3, (d.total / maxBar) * 100)}%` }} />
                        <span>{d.day}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="apd-sec" data-testid="apd-tips">
                  <div className="head"><h3>Conseils pour bien démarrer</h3><button onClick={() => setEditor(nextStep)}>Voir tous <ChevronRight size={13} /></button></div>
                  <button className="apd-tip" onClick={() => setEditor("photos")} data-testid="tip-photos"><span className="ic" style={{ background: "#E8F8EF" }}><Camera size={17} color="#0E8A4F" /></span><div><b>Ajoutez de belles photos</b><span>Montrez vos produits et votre emplacement.</span></div><ChevronRight size={15} style={{ marginLeft: "auto", color: "#9CA3AF" }} /></button>
                  <button className="apd-tip" onClick={() => setEditor("produits")} data-testid="tip-products"><span className="ic" style={{ background: "#FDF3E3" }}><Tag size={17} color="#F0590B" /></span><div><b>Définissez vos produits et tarifs</b><span>Indiquez vos prix et descriptions.</span></div><ChevronRight size={15} style={{ marginLeft: "auto", color: "#9CA3AF" }} /></button>
                  <button className="apd-tip" onClick={() => setEditor("livraison")} data-testid="tip-delivery"><span className="ic" style={{ background: "#E8F8EF" }}><MapPin size={17} color="#0E8A4F" /></span><div><b>Définissez votre zone de livraison</b><span>Indiquez les quartiers où vous livrez.</span></div><ChevronRight size={15} style={{ marginLeft: "auto", color: "#9CA3AF" }} /></button>
                  <button className="apd-tip" onClick={() => setEditor("publication")} data-testid="tip-publish"><span className="ic" style={{ background: "#EAF2FB" }}><Eye size={17} color="#2563EB" /></span><div><b>Publiez votre fiche</b><span>Devenez visible sur AYA et recevez des clients.</span></div><ChevronRight size={15} style={{ marginLeft: "auto", color: "#9CA3AF" }} /></button>
                </div>
                <div className="apd-sec" data-testid="apd-preview-card">
                  <div className="head"><h3>Aperçu de ma fiche publique</h3><button onClick={openPreview} data-testid="apd-preview-btn"><Eye size={13} /> Aperçu</button></div>
                  <div style={{ display: "flex", gap: 11, marginTop: 11, alignItems: "center" }}>
                    <span className="av" style={{ width: 52, height: 52, borderRadius: "50%", background: "#E8F8EF", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>{shop.logo_image ? <img src={shop.logo_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🏪"}</span>
                    <div><b style={{ fontSize: 14 }}>{shop.name}</b><div style={{ fontSize: 11.5, color: "#6B7280" }}>{shop.description || shop.tagline || "—"}</div>
                      <span className="apd-status draft" style={{ marginTop: 5 }}>⏳ Fiche non publiée</span></div>
                  </div>
                  <div style={{ display: "flex", gap: 10, background: "#FFF7E6", borderRadius: 13, padding: 11, marginTop: 12, fontSize: 11.5, color: "#374151", lineHeight: 1.45 }}>
                    <span>🙈</span> Votre fiche n'est pas encore visible sur AYA. Terminez la configuration pour commencer à recevoir des clients.
                  </div>
                </div>
              </>
            )}

            {Yango}
            <button className="apd-banner" style={{ background: "#E8F8EF" }} onClick={() => setTab("stats")} data-testid="apd-grow-banner">
              <span className="ic" style={{ background: "#0E8A4F", color: "#fff" }}><BarChart3 size={19} /></span>
              <div><b>{published ? "Attirez plus de clients" : "Astuces pour attirer plus de clients"}</b><span>{published ? "Mettez en avant votre activité sur AYA et gagnez en visibilité." : "Découvrez nos conseils pour réussir sur AYA."}</span></div>
              <ChevronRight size={16} style={{ marginLeft: "auto", flexShrink: 0 }} />
            </button>
          </>
        )}

        {tab === "produits" && (
          <div className="apd-sec" style={{ marginTop: 10 }} data-testid="apd-tab-produits">
            <div className="head"><h3>Mes produits ({products.length})</h3><button onClick={() => setEditor("produits")} data-testid="tab-prod-manage"><Plus size={13} /> Gérer</button></div>
            {products.length === 0 && <div className="apd-empty">Aucun produit. Ajoutez votre premier produit.</div>}
            {products.map((p) => (
              <div className="apd-pline" key={p.id}>
                {p.image ? <img src={p.image} alt="" /> : <span style={{ width: 44, height: 44, borderRadius: 11, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>🛒</span>}
                <div className="nm"><b>{p.name}</b><span>{fcfa(p.price)} FCFA · {p.category || "Sans catégorie"}</span></div>
                <button className={`apd-chip ${p.in_stock ? "livree" : "annulee"}`} onClick={() => updateProduct(p.id, { in_stock: !p.in_stock })}>{p.in_stock ? "En stock" : "Rupture"}</button>
              </div>
            ))}
          </div>
        )}

        {tab === "commandes" && (
          <div className="apd-sec" style={{ marginTop: 10 }} data-testid="apd-tab-commandes">
            <div className="head"><h3>Commandes ({ordersList.length})</h3></div>
            {ordersList.length === 0 && <div className="apd-empty">Aucune commande pour le moment.</div>}
            {ordersList.map((o) => (
              <div className="apd-order" key={o.id}>
                <span className="av">{initials(o.customer_name)}</span>
                <div className="nm"><b>{o.customer_name} · {fcfa(o.total)} FCFA</b><span>{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")} · {o.delivery_mode === "livraison" ? "🛵 Livraison" : "🏪 Retrait"} · {relTime(o.created_at)}</span></div>
                <button className={`apd-chip ${o.status}`} onClick={() => advanceOrder(o)} data-testid={`tab-order-status-${o.id}`}>● {STATUS_LABEL[o.status]}{NEXT_STATUS[o.status] ? " →" : ""}</button>
              </div>
            ))}
          </div>
        )}

        {tab === "clients" && (
          <div className="apd-sec" style={{ marginTop: 10 }} data-testid="apd-tab-clients">
            <div className="head"><h3>Clients ({clients.length})</h3></div>
            {clients.length === 0 && <div className="apd-empty">Vos clients apparaîtront ici après leur première commande.</div>}
            {clients.map((c) => (
              <div className="apd-order" key={c.phone}>
                <span className="av">{initials(c.name)}</span>
                <div className="nm"><b>{c.name}</b><span>{c.phone} · {c.count} commande{c.count > 1 ? "s" : ""}</span></div>
                <b style={{ fontSize: 13, color: "#0E6A3D" }}>{fcfa(c.total)} F</b>
              </div>
            ))}
          </div>
        )}

        {tab === "stats" && stats && (
          <div data-testid="apd-tab-stats">
            <div className="apd-kpis" style={{ marginTop: 10 }}>
              <div className="apd-kpi" style={{ background: "#E8F8EF" }}><span className="ic"><ShoppingCart size={17} color="#0E8A4F" /></span><b>{stats.orders_week}</b><span>Commandes cette semaine</span></div>
              <div className="apd-kpi" style={{ background: "#FDF3E3" }}><span className="ic"><Users size={17} color="#F0590B" /></span><b>{stats.clients_month}</b><span>Clients ce mois</span></div>
              <div className="apd-kpi" style={{ background: "#EAF2FB" }}><span className="ic"><BarChart3 size={17} color="#2563EB" /></span><b>{fcfa(stats.revenue_month)}</b><span>FCFA ce mois</span></div>
              <div className="apd-kpi" style={{ background: "#E9F7FB" }}><span className="ic"><Eye size={17} color="#0891B2" /></span><b>{fcfa(stats.views)}</b><span>Vues de ma fiche</span></div>
            </div>
            <div className="apd-sec">
              <div className="head"><h3>Performance de la semaine</h3></div>
              <div className="apd-chart">
                {stats.week_series.map((d, i) => (
                  <div className="apd-bar" key={i}><i style={{ height: `${Math.max(3, (d.total / maxBar) * 100)}%` }} /><span>{d.day}</span></div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 12, fontWeight: 800, color: "#374151" }}>
                <span><Users size={12} style={{ verticalAlign: -2 }} /> {stats.followers} abonnés</span>
                <span><CheckCircle2 size={12} style={{ verticalAlign: -2 }} color="#0E8A4F" /> Boutique {published ? "publiée" : "non publiée"}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="apd-nav" data-testid="apd-bottom-nav">
        <button className={tab === "accueil" ? "on" : ""} onClick={() => setTab("accueil")} data-testid="nav-accueil"><Home size={19} /> Accueil</button>
        <button className={tab === "produits" ? "on" : ""} onClick={() => setTab("produits")} data-testid="nav-produits"><Package size={19} /> Produits</button>
        <button className={tab === "commandes" ? "on" : ""} onClick={() => setTab("commandes")} data-testid="nav-commandes"><ClipboardList size={19} /> {published ? "Commandes" : "Demandes"}</button>
        <button className={tab === "clients" ? "on" : ""} onClick={() => setTab("clients")} data-testid="nav-clients"><Users size={19} /> {published ? "Clients" : "Rendez-vous"}</button>
        <button className={tab === "stats" ? "on" : ""} onClick={() => setTab("stats")} data-testid="nav-stats"><BarChart3 size={19} /> Statistiques</button>
      </div>

      {editor === "infos" && <InfosEditor shop={shop} onSave={saveShop} onClose={() => setEditor(null)} />}
      {editor === "photos" && <PhotosEditor shop={shop} onSave={saveShop} onClose={() => setEditor(null)} />}
      {editor === "produits" && <ProductsEditor products={products} onCreate={createProduct} onUpdate={updateProduct} onDelete={deleteProduct} onClose={() => setEditor(null)} />}
      {editor === "livraison" && <DeliveryEditor shop={shop} onSave={saveShop} onClose={() => setEditor(null)} />}
      {editor === "publication" && <PublishEditor shop={shop} onPublish={publish} onPreview={openPreview} onClose={() => setEditor(null)} />}

      {liveSheet && (
        <>
          <div className="apd-sheet-ovl" onClick={() => setLiveSheet(false)} />
          <div className="apd-sheet" data-testid="apd-live-sheet">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div><h3>🔴 LIVE Boutique</h3><p className="sub">Interactions et commandes en temps réel. Diffusion vidéo (LiveKit/Agora) : dépendance à configurer — non simulée.</p></div>
              <button onClick={() => setLiveSheet(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", fontSize: 18 }} data-testid="live-sheet-close">✕</button>
            </div>
            {!liveData.live ? (
              <>
                <div className="apd-input"><label>Titre du direct</label><input value={liveTitle} onChange={(e) => setLiveTitle(e.target.value)} placeholder={`En direct de ${shop.name}`} data-testid="live-title-input" /></div>
                <button className="apd-save" style={{ background: "#DC2626" }} onClick={startLive} data-testid="live-start">🔴 Démarrer le direct</button>
              </>
            ) : (
              <>
                <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap", fontSize: 12.5, fontWeight: 900 }}>
                  <span className="apd-chip livree" data-testid="live-viewers-pro">👁 {liveData.live.viewers} spectateurs</span>
                  <span className="apd-chip en_cours">❤️ {liveData.live.reactions} réactions</span>
                  <span className="apd-chip en_attente">🛒 {liveData.live_orders.length} commande(s) live</span>
                </div>
                <div className="apd-input">
                  <label>Produit mis en avant</label>
                  {products.map((p) => (
                    <button key={p.id} className="apd-pline" style={{ width: "100%", background: liveData.live.pinned_product?.id === p.id ? "#F0FBF4" : "none", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left", borderRadius: 11, padding: "8px 6px" }} onClick={() => pinProduct(liveData.live.pinned_product?.id === p.id ? null : p.id)} data-testid={`live-pin-${p.id}`}>
                      {p.image ? <img src={p.image} alt="" /> : <span style={{ width: 44, height: 44, borderRadius: 11, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>🛒</span>}
                      <div className="nm"><b>{p.name}</b><span>{fcfa(p.price)} FCFA</span></div>
                      <span style={{ fontSize: 11, fontWeight: 900, color: liveData.live.pinned_product?.id === p.id ? "#0E6A3D" : "#9CA3AF" }}>{liveData.live.pinned_product?.id === p.id ? "📌 Épinglé" : "Épingler"}</span>
                    </button>
                  ))}
                </div>
                <div className="apd-input">
                  <label>Messages des spectateurs</label>
                  <div style={{ maxHeight: 120, overflowY: "auto", background: "#F9FAF9", borderRadius: 12, padding: 10, display: "grid", gap: 5 }} data-testid="live-pro-messages">
                    {liveData.live.messages.length === 0 && <span style={{ fontSize: 11.5, color: "#9CA3AF" }}>Aucun message pour le moment.</span>}
                    {liveData.live.messages.map((m) => <div key={m.id} style={{ fontSize: 11.5 }}><b style={{ color: "#0E6A3D" }}>{m.name}</b> {m.text}</div>)}
                  </div>
                </div>
                {liveData.live_orders.length > 0 && (
                  <div className="apd-input">
                    <label>Commandes reçues pendant le direct</label>
                    {liveData.live_orders.map((o) => (
                      <div className="apd-order" key={o.id} data-testid={`live-order-${o.id}`}>
                        <span className="av">{initials(o.customer_name)}</span>
                        <div className="nm"><b>{o.customer_name} · {fcfa(o.total)} FCFA</b><span>{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</span></div>
                      </div>
                    ))}
                  </div>
                )}
                <button className="apd-save" style={{ background: "#111827" }} onClick={stopLive} data-testid="live-stop">■ Terminer le direct</button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
